import { useCallback, useMemo } from "react";

import { AnimatePresence, m } from "motion/react";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";

import { InsightsCopilotButton } from "src/analytics/copilot/InsightsCopilotButton";
import { useChartExplore } from "src/analytics/copilot/useChartExplore";
import { DashboardFormModal } from "src/analytics/dashboard/DashboardFormModal";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardMetricsProvider } from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardRefreshButton } from "src/analytics/dashboard/DashboardRefreshButton";
import { DashboardTabs } from "src/analytics/dashboard/DashboardTabs";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useWorkspaceSpecialDomainLabels } from "src/analytics/workspace/useWorkspaceSpecialDomainLabels";
import { WorkspaceCopilotSidebar } from "src/analytics/workspace/WorkspaceCopilotSidebar";
import {
  useDeleteDashboard,
  useListDashboardsQuery,
} from "src/api/analytics-dashboard/dashboard";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { Realm } from "src/clients/analytics-dashboard";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { PageHeader } from "src/layout/PageHeader";
import { SubHeaderDatePicker } from "src/layout/SubHeader";
import { DashboardPageParams } from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const WorkspaceInsightsContent: React.FC = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const labelDictionaries = useWorkspaceSpecialDomainLabels(wsId);
  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(
      defaultWindow,
      "workspaceInsightsTimeWindow",
    );

  const [isCopilotOpen, setIsCopilotOpen] = useSessionStorage(
    "workspaceInsightsCopilotOpen",
    false,
  );
  const openCopilot = useCallback(
    () => setIsCopilotOpen(true),
    [setIsCopilotOpen],
  );
  const { pendingChartContext, exploreWithCopilot, clearPendingContext } =
    useChartExplore(openCopilot);

  const { data: dashboards } = useListDashboardsQuery(Realm.WORKSPACE, wsId);
  const [activeDashboardId, setActiveDashboardId] = useLocalStorage<
    string | undefined
  >(`workspaceInsightsActiveDashboard:${wsId}`, undefined);
  const resolvedDashboardId =
    activeDashboardId && dashboards?.some((d) => d.id === activeDashboardId)
      ? activeDashboardId
      : dashboards?.[0]?.id;

  const { workspaceInsights } = useCapabilities();
  const { dashboard } = useDashboard(
    Realm.WORKSPACE,
    wsId,
    resolvedDashboardId,
    { enabled: resolvedDashboardId !== undefined },
  );
  const pins = resolvedDashboardId ? Object.values(dashboard.pins) : [];

  const {
    isOpen: isCreateOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
    afterLeave: createAfterLeave,
  } = useModal();

  const {
    isOpen: isEditOpen,
    data: editingDashboardId,
    openModal: openEditModal,
    closeModal: closeEditModal,
    afterLeave: editAfterLeave,
  } = useModal<string>();

  const {
    isOpen: isDeleteOpen,
    data: deletingDashboard,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    afterLeave: deleteAfterLeave,
  } = useModal<{ id: string; name: string; isLast: boolean }>();

  const deleteDashboard = useDeleteDashboard(Realm.WORKSPACE, wsId);
  const deletingDashboardName = deletingDashboard?.name ?? "Untitled Dashboard";
  const deleteDescription = [
    `This permanently deletes "${deletingDashboardName}" and its charts.`,
    deletingDashboard?.isLast &&
      "Insights will start again with a new empty dashboard.",
    "Type the dashboard name to confirm.",
  ]
    .filter(Boolean)
    .join(" ");

  const handleDeleteConfirm = async () => {
    if (!deletingDashboard) return;
    try {
      await deleteDashboard.mutateAsync(deletingDashboard.id);
      if (activeDashboardId === deletingDashboard.id) {
        setActiveDashboardId(undefined);
      }
      toastActions.success({ title: "Successfully deleted dashboard" });
    } catch {
      toastActions.failure({ title: "Error deleting dashboard" });
    }
    closeDeleteModal();
  };

  return (
    <JustPinnedProvider key={`${Realm.WORKSPACE}:${wsId}`}>
      <DashboardMetricsProvider
        // Workspace insights aggregate flow data, so they read the flow analytics context.
        dataSource="flow_analytics"
        pins={pins}
        timeWindow={timeWindow}
      >
        <div className="flex h-full min-h-0">
          <div
            className="flex h-full min-w-0 flex-1 flex-col"
            data-loc="workspace-insights-content"
          >
            <DocumentTitle segments={["Insights"]} />
            <PageHeader
              title="Insights"
              sideContent={
                <AnimatePresence initial={false}>
                  {!isCopilotOpen && (
                    <m.div
                      animate={{ opacity: 1, scale: 1 }}
                      className="pr-3"
                      exit={{ opacity: 0, scale: 0.95 }}
                      initial={{ opacity: 0, scale: 0.95 }}
                      key="ask-copilot"
                      transition={{ duration: 0.15 }}
                    >
                      <InsightsCopilotButton
                        dataLoc="workspace-insights-ask-copilot-button"
                        onClick={openCopilot}
                      />
                    </m.div>
                  )}
                </AnimatePresence>
              }
            />
            <DashboardTabs
              activeDashboardId={resolvedDashboardId ?? ""}
              canCreate={workspaceInsights.canEdit}
              realm={Realm.WORKSPACE}
              realmId={wsId}
              onCreateRequest={() => openCreateModal()}
              onDashboardChange={setActiveDashboardId}
              onDeleteRequest={
                workspaceInsights.canEdit
                  ? (dashboardId) =>
                      openDeleteModal({
                        id: dashboardId,
                        name:
                          dashboards?.find((d) => d.id === dashboardId)?.name ??
                          "Untitled Dashboard",
                        isLast: dashboards?.length === 1,
                      })
                  : undefined
              }
              onEditRequest={
                workspaceInsights.canEdit ? openEditModal : undefined
              }
            >
              <SubHeaderDatePicker
                placeholder="Last 30 days"
                value={dateRangePickerValue}
                onChange={onDateRangePickerChange}
              />
              <DashboardRefreshButton />
            </DashboardTabs>
            <div className="flex h-full flex-col overflow-auto p-5">
              {resolvedDashboardId ? (
                <DashboardGrid
                  dashboardId={resolvedDashboardId}
                  labelDictionaries={labelDictionaries}
                  realm={Realm.WORKSPACE}
                  realmId={wsId}
                  onExploreWithCopilot={exploreWithCopilot}
                  onOpenCopilot={openCopilot}
                />
              ) : (
                <DashboardGridSkeleton />
              )}
            </div>
            <DashboardFormModal
              afterLeave={createAfterLeave}
              open={isCreateOpen}
              realm={Realm.WORKSPACE}
              realmId={wsId}
              onClose={closeCreateModal}
              onCreated={setActiveDashboardId}
            />
            <DashboardFormModal
              afterLeave={editAfterLeave}
              dashboardId={editingDashboardId}
              open={isEditOpen}
              realm={Realm.WORKSPACE}
              realmId={wsId}
              onClose={closeEditModal}
            />
            <TextConfirmationModal
              afterLeave={deleteAfterLeave}
              challengeText={deletingDashboardName}
              confirmButtonText="Delete"
              label="Dashboard name"
              open={isDeleteOpen}
              title="Delete this dashboard?"
              description={deleteDescription}
              onClose={closeDeleteModal}
              onConfirm={handleDeleteConfirm}
            />
          </div>
          <AnimatePresence initial={false} mode="wait">
            {isCopilotOpen && (
              <WorkspaceCopilotSidebar
                dashboardId={resolvedDashboardId}
                key="workspace-copilot-sidebar"
                labelDictionaries={labelDictionaries}
                pendingStructuredInput={pendingChartContext}
                wsId={wsId}
                onClearPendingStructuredInput={clearPendingContext}
                onClose={() => setIsCopilotOpen(false)}
              />
            )}
          </AnimatePresence>
        </div>
      </DashboardMetricsProvider>
    </JustPinnedProvider>
  );
};
